import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline library picker — portalled to document.body so parent
 * `backdrop-filter` / `transform` never traps the fixed positioning.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - kind: "product" | "avatar" | "logo"
 *  - allowMultiple: boolean (default true)
 *  - onConfirm: (assets: Array<{id, s3_url, name, ...}>) => void
 */
const KIND_META = {
  product: { icon: '📦', label: 'Product Image', title: 'Pick products from your library' },
  avatar:  { icon: '👤', label: 'Founder / Avatar', title: 'Pick avatars from your library' },
  logo:    { icon: '🏷️', label: 'Brand Logo', title: 'Pick a logo from your library' },
};

const AssetLibraryPickerModal = ({ isOpen, onClose, kind, allowMultiple = true, onConfirm }) => {
  const { colorTheme } = useColorTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setSelectedIds([]); setQuery('');
      try {
        const token = localStorage.getItem('session_token');
        const res = await axios.get(`${API}/asset-library`, {
          params: { kind },
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (!cancelled) setItems(res.data?.items || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, kind]);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  // Theme mapping — match the rest of the app (emerald / purple / blue)
  const themePick = (g, p, b) => (colorTheme === 'purple' ? p : colorTheme === 'blue' ? b : g);
  const ctaGradient = themePick(
    'from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
    'from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600',
    'from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600',
  );
  const selectedRing = themePick('ring-emerald-400/60 border-emerald-500', 'ring-purple-400/60 border-purple-500', 'ring-blue-400/60 border-blue-500');
  const selectedBadge = themePick('bg-emerald-500', 'bg-purple-500', 'bg-blue-500');
  const focusRing = themePick('focus:ring-emerald-500 focus:border-emerald-500', 'focus:ring-purple-500 focus:border-purple-500', 'focus:ring-blue-500 focus:border-blue-500');
  const accentText = themePick('text-emerald-600 dark:text-emerald-300', 'text-purple-600 dark:text-purple-300', 'text-blue-600 dark:text-blue-300');
  const hoverBorder = themePick('hover:border-emerald-300', 'hover:border-purple-300', 'hover:border-blue-300');

  const meta = KIND_META[kind] || KIND_META.product;

  const filtered = query.trim()
    ? items.filter(i =>
        (i.name || '').toLowerCase().includes(query.toLowerCase()) ||
        (i.source_domain || '').toLowerCase().includes(query.toLowerCase()))
    : items;

  const toggle = (id) => {
    if (allowMultiple) {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelectedIds([id]);
    }
  };

  const handleConfirm = () => {
    const chosen = items.filter(i => selectedIds.includes(i.id));
    if (chosen.length > 0 && onConfirm) onConfirm(chosen);
    onClose();
  };

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center bg-black/60 sm:backdrop-blur-sm animate-fadeIn"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="asset-library-picker-backdrop"
    >
      <div
        className="relative bg-white dark:bg-gray-800 w-full sm:w-[95vw] sm:max-w-4xl h-full sm:h-auto sm:max-h-[85vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scaleIn"
        style={{ minHeight: '0' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="min-w-0 pr-3">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span className="text-xl sm:text-2xl">{meta.icon}</span>
              <span className="truncate">{meta.title}</span>
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {allowMultiple ? 'Tap to select one or more. They stay in your library.' : 'Tap the one you want.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="asset-library-picker-close"
            className="w-9 h-9 flex-shrink-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 text-lg"
            aria-label="Close"
          >✕</button>
        </div>

        {/* Search */}
        <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or site…"
            data-testid="asset-library-picker-search"
            className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 ${focusRing}`}
          />
        </div>

        {/* Grid — the ONLY scrollable region */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 min-h-0">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">⚠️ {error}</div>
          )}
          {loading ? (
            <div className="py-16 text-center text-slate-500 dark:text-slate-400">Loading library…</div>
          ) : filtered.length === 0 ? (
            <div className="py-14 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl">
              <div className="text-5xl mb-3">{meta.icon}</div>
              <p className="text-slate-600 dark:text-slate-300 font-medium">
                {items.length === 0 ? 'Your library is empty' : 'No matches'}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {items.length === 0
                  ? `Extract a website and any ${kind} images will land here.`
                  : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {filtered.map(asset => {
                const sel = selectedIds.includes(asset.id);
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => toggle(asset.id)}
                    data-testid={`asset-library-picker-item-${asset.id.slice(0,8)}`}
                    className={`group relative rounded-xl overflow-hidden border-2 text-left transition-colors ${
                      sel
                        ? `${selectedRing} ring-2`
                        : `border-slate-200 dark:border-slate-700 ${hoverBorder}`
                    }`}
                  >
                    <div className="aspect-square bg-slate-100 dark:bg-slate-900">
                      <img src={asset.s3_url} alt={asset.name || 'asset'} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium truncate text-slate-800 dark:text-slate-100" title={asset.name}>
                        {asset.name || <span className="italic text-slate-400">Untitled</span>}
                      </p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                        {asset.source_domain || '—'}
                      </p>
                    </div>
                    {sel && (
                      <div className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full ${selectedBadge} text-white text-xs flex items-center justify-center shadow`}>✓</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — always visible */}
        <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 flex-shrink-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {selectedIds.length > 0
              ? <><strong className={accentText}>{selectedIds.length}</strong> selected</>
              : (items.length > 0 ? 'Tap tiles to select' : 'Nothing to pick yet')}
          </p>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="asset-library-picker-cancel"
              className="px-3 sm:px-4 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedIds.length === 0}
              data-testid="asset-library-picker-confirm"
              className={`px-4 sm:px-5 py-2.5 rounded-xl bg-gradient-to-r ${ctaGradient} text-white text-sm font-semibold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all transform hover:scale-105 disabled:transform-none flex items-center gap-2`}
            >
              <span>+</span>
              <span className="whitespace-nowrap">
                {selectedIds.length > 0
                  ? `Add ${selectedIds.length} to campaign`
                  : 'Add to campaign'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn { from { transform: scale(0.97); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out }
        .animate-scaleIn { animation: scaleIn 0.25s ease-out }
      `}</style>
    </div>
  );

  // Portal to <body> so no ancestor with `backdrop-filter` / `transform`
  // can turn our `position: fixed` into `position: absolute`.
  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(modal, document.body)
    : modal;
};

export default AssetLibraryPickerModal;
